import type {
  AdminAccount,
  AdminInvite,
  AdminPermission,
  AdminRole,
  AdminRoleDefinition,
  AdminTeamMember,
  AdminVenue,
  CustomerAccount,
  DashboardSummary,
  EventCategory,
  EventDashboard,
  EventApprovalStatus,
  EventItem,
  MobileMoneyNetwork,
  Order,
  OrderRegistrationForm,
  PaymentSession,
  OrganizerApplication,
  OrganizerApplicationStatus,
  PaymentMethod,
  SignupRange,
  SiteContent,
  UsersDashboard,
} from "../types/api";

const API_URL = (
  import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? "http://localhost:4000/api" : "/api")
).replace(/\/+$/, "");

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

type RequestOptions = RequestInit & {
  token?: string | null;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { token, headers, ...init } = options;
  const requestHeaders = new Headers(headers);

  if (token) {
    requestHeaders.set("Authorization", `Bearer ${token}`);
  }

  if (typeof init.body === "string" && !requestHeaders.has("Content-Type")) {
    requestHeaders.set("Content-Type", "application/json");
  }

  const response = await fetch(`${API_URL}${path}`, {
    cache: "no-store",
    ...init,
    headers: requestHeaders,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({ message: "Request failed" }))) as { message?: string };
    throw new ApiError(response.status, body.message ?? "Request failed");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

function queryString(params: Record<string, string | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      search.set(key, value);
    }
  });
  const output = search.toString();
  return output ? `?${output}` : "";
}

export type DashboardFilters = {
  purchaseStart?: string;
  purchaseEnd?: string;
  eventStart?: string;
  eventEnd?: string;
};

export const publicApi = {
  listEvents: (params?: { search?: string; category?: string; featured?: boolean; period?: "upcoming" | "past" }) =>
    request<{ events: EventItem[] }>(
      `/events${queryString({
        search: params?.search,
        category: params?.category,
        featured: params?.featured ? "true" : undefined,
        period: params?.period,
      })}`
    ),
  getEvent: (id: string) => request<{ event: EventItem }>(`/events/${id}`),
  getSiteContent: () => request<{ content: SiteContent }>("/site-content"),
  lookupOrders: (params: { email?: string; reference?: string }) =>
    request<{ orders: Order[] }>(
      `/orders/lookup${queryString({
        email: params.email,
        reference: params.reference,
      })}`
    ),
};

export const organizerApi = {
  apply: (payload: {
    name: string;
    email: string;
    phone: string;
    businessName: string;
    description?: string;
    website?: string;
    city?: string;
    password: string;
  }) =>
    request<{ application: OrganizerApplication }>("/exhibitor/apply", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  status: (email: string) =>
    request<{ application: OrganizerApplication | null }>(
      `/exhibitor/applications/status${queryString({ email })}`
    ),
};

export const customerApi = {
  register: (payload: { name: string; email: string; phone: string; password: string }) =>
    request<{ token: string; user: CustomerAccount; sessionExpiresInMinutes: number }>("/auth/customer/register", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  login: (payload: { email: string; password: string }) =>
    request<{ token: string; user: CustomerAccount; sessionExpiresInMinutes: number }>("/auth/customer/login", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  socialLogin: (payload: { provider: "google" | "apple"; idToken: string; name?: string }) =>
    request<{ token: string; user: CustomerAccount; sessionExpiresInMinutes: number }>("/auth/customer/social", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  me: (token: string) =>
    request<{ user: CustomerAccount; sessionExpiresInMinutes: number }>("/auth/customer/me", { token }),
  checkout: (
    token: string,
    payload: {
      eventId: string;
      customer: { name: string; email: string; phone: string };
      items: Array<{ ticketTierId: string; quantity: number }>;
      paymentMethod: PaymentMethod;
      mobileNetwork?: MobileMoneyNetwork;
      registrationForm?: OrderRegistrationForm | null;
    }
  ) =>
    request<{ order: Order; paymentSession: PaymentSession }>("/orders/checkout", {
      token,
      method: "POST",
      body: JSON.stringify(payload),
    }),
  verifyPayment: (token: string, reference: string, payload?: { reference?: string }) =>
    request<{ order: Order }>(`/orders/${reference}/payment/verify`, {
      token,
      method: "POST",
      body: JSON.stringify(payload ?? {}),
    }),
  resumePayment: (token: string, reference: string) =>
    request<{ order: Order; paymentSession?: PaymentSession }>(`/orders/${reference}/payment/resume`, {
      token,
      method: "POST",
      body: JSON.stringify({}),
    }),
  myOrders: (token: string, params?: { reference?: string }) =>
    request<{ orders: Order[] }>(
      `/orders/my${queryString({
        reference: params?.reference,
      })}`,
      { token }
    ),
};

export const adminApi = {
  login: (payload: { email: string; password: string }) =>
    request<{ token: string; admin: AdminAccount }>("/auth/login", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  me: (token: string) => request<{ admin: AdminAccount }>("/auth/me", { token }),
  dashboard: (token: string, filters?: DashboardFilters) =>
    request<DashboardSummary>(`/admin/dashboard${queryString(filters ?? {})}`, { token }),
  listEvents: (token: string) => request<{ events: EventItem[] }>("/admin/events", { token }),
  eventDashboard: (token: string, id: string) =>
    request<EventDashboard>(`/admin/events/${id}/dashboard`, { token }),
  saveEvent: (token: string, payload: Partial<EventItem>, id?: string) =>
    request<{ event: EventItem }>(id ? `/admin/events/${id}` : "/admin/events", {
      token,
      method: id ? "PUT" : "POST",
      body: JSON.stringify(payload),
    }),
  deleteEvent: (token: string, id: string) =>
    request<void>(`/admin/events/${id}`, {
      token,
      method: "DELETE",
    }),
  getSiteContent: (token: string) => request<{ content: SiteContent }>("/admin/site-content", { token }),
  updateSiteContent: (token: string, payload: Partial<SiteContent>) =>
    request<{ content: SiteContent }>("/admin/site-content", {
      token,
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  listEventApprovals: (token: string, status?: EventApprovalStatus) =>
    request<{ events: EventItem[] }>(
      `/admin/event-approvals${queryString({ status })}`,
      { token }
    ),
  approveEventApproval: (token: string, id: string, notes?: string) =>
    request<{ event: EventItem }>(`/admin/event-approvals/${id}/approve`, {
      token,
      method: "POST",
      body: JSON.stringify({ notes: notes ?? "" }),
    }),
  rejectEventApproval: (token: string, id: string, notes?: string) =>
    request<{ event: EventItem }>(`/admin/event-approvals/${id}/reject`, {
      token,
      method: "POST",
      body: JSON.stringify({ notes: notes ?? "" }),
    }),
  listOrders: (token: string) => request<{ orders: Order[] }>("/admin/orders", { token }),
  getOrder: (token: string, reference: string) =>
    request<{ order: Order }>(`/admin/orders/${reference}`, { token }),
  downloadOrderTicketsPdf: async (token: string, reference: string): Promise<Blob> => {
    const response = await fetch(`${API_URL}/admin/orders/${reference}/tickets.pdf`, {
      cache: "no-store",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({ message: "Could not download tickets" }))) as {
        message?: string;
      };
      throw new ApiError(response.status, body.message ?? "Could not download tickets");
    }
    return response.blob();
  },
  issueTickets: (
    token: string,
    eventId: string,
    payload: {
      items: Array<{ ticketTierId: string; quantity: number; attendees?: string[] }>;
      customer?: { name?: string; email?: string; phone?: string };
      paymentMethod: "cash" | "card" | "comp" | "mobile-money" | "bank-transfer";
      notes?: string;
    }
  ) =>
    request<{ order: Order }>(`/admin/events/${eventId}/issue-tickets`, {
      token,
      method: "POST",
      body: JSON.stringify(payload),
    }),
  scanTicket: (token: string, ticketCode: string, eventId: string) =>
    request<{
      message: string;
      orderReference: string;
      event: Order["event"];
      ticket: Order["tickets"][number];
    }>("/admin/tickets/scan", {
      token,
      method: "POST",
      body: JSON.stringify({ ticketCode, eventId }),
    }),
  uploadImage: async (token: string, file: File): Promise<{ url: string; publicId: string }> => {
    const form = new FormData();
    form.append("file", file);

    const response = await fetch(`${API_URL}/admin/uploads/image`, {
      method: "POST",
      cache: "no-store",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({ message: "Upload failed" }))) as {
        message?: string;
      };
      throw new ApiError(response.status, body.message ?? "Upload failed");
    }

    return (await response.json()) as { url: string; publicId: string };
  },
  uploadVideo: async (token: string, file: File): Promise<{ url: string; publicId: string }> => {
    const form = new FormData();
    form.append("file", file);

    const response = await fetch(`${API_URL}/admin/uploads/video`, {
      method: "POST",
      cache: "no-store",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({ message: "Upload failed" }))) as {
        message?: string;
      };
      throw new ApiError(response.status, body.message ?? "Upload failed");
    }

    return (await response.json()) as { url: string; publicId: string };
  },
  listUsers: (token: string, range?: SignupRange) =>
    request<UsersDashboard>(`/admin/users${queryString({ range })}`, { token }),
  listCatalog: (token: string) =>
    request<{ categories: EventCategory[]; venues: AdminVenue[] }>("/admin/catalog", { token }),
  createCategory: (
    token: string,
    payload: { name: string; description?: string; active?: boolean }
  ) =>
    request<{ category: EventCategory }>("/admin/categories", {
      token,
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateCategory: (
    token: string,
    id: string,
    payload: { name?: string; description?: string; active?: boolean }
  ) =>
    request<{ category: EventCategory }>(`/admin/categories/${id}`, {
      token,
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  deleteCategory: (token: string, id: string) =>
    request<void>(`/admin/categories/${id}`, {
      token,
      method: "DELETE",
    }),
  createVenue: (
    token: string,
    payload: {
      name: string;
      city: string;
      address: string;
      capacity?: number;
      notes?: string;
      active?: boolean;
    }
  ) =>
    request<{ venue: AdminVenue }>("/admin/venues", {
      token,
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateVenue: (
    token: string,
    id: string,
    payload: {
      name?: string;
      city?: string;
      address?: string;
      capacity?: number;
      notes?: string;
      active?: boolean;
    }
  ) =>
    request<{ venue: AdminVenue }>(`/admin/venues/${id}`, {
      token,
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  deleteVenue: (token: string, id: string) =>
    request<void>(`/admin/venues/${id}`, {
      token,
      method: "DELETE",
    }),
  listTeam: (token: string) =>
    request<{ admins: AdminTeamMember[] }>("/admin/team", { token }),
  listInvites: (token: string) =>
    request<{ invites: AdminInvite[] }>("/admin/invites", { token }),
  createInvite: (
    token: string,
    payload: { email: string; role: AdminRole; permissions?: AdminPermission[]; eventIds?: string[] }
  ) =>
    request<{ invite: AdminInvite; token: string }>("/admin/invites", {
      token,
      method: "POST",
      body: JSON.stringify(payload),
    }),
  revokeInvite: (token: string, inviteId: string) =>
    request<{ invite: AdminInvite }>(`/admin/invites/${inviteId}`, {
      token,
      method: "DELETE",
    }),
  updateTeamMember: (
    token: string,
    adminId: string,
    payload: { role?: AdminRole; permissions?: AdminPermission[] }
  ) =>
    request<{ admin: AdminTeamMember }>(`/admin/team/${adminId}`, {
      token,
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  removeTeamMember: (token: string, adminId: string) =>
    request<void>(`/admin/team/${adminId}`, {
      token,
      method: "DELETE",
    }),
  listRoles: (token: string) =>
    request<{ roles: AdminRoleDefinition[] }>("/admin/roles", { token }),
  createRole: (
    token: string,
    payload: { label: string; description?: string; permissions: AdminPermission[] }
  ) =>
    request<{ role: AdminRoleDefinition }>("/admin/roles", {
      token,
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateRole: (
    token: string,
    roleId: string,
    payload: { label?: string; description?: string; permissions?: AdminPermission[] }
  ) =>
    request<{ role: AdminRoleDefinition }>(`/admin/roles/${roleId}`, {
      token,
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  deleteRole: (token: string, roleId: string) =>
    request<void>(`/admin/roles/${roleId}`, {
      token,
      method: "DELETE",
    }),
  listOrganizerApplications: (token: string, status?: OrganizerApplicationStatus) =>
    request<{ applications: OrganizerApplication[] }>(
      `/admin/exhibitor-applications${queryString({ status })}`,
      { token }
    ),
  approveOrganizerApplication: (token: string, id: string) =>
    request<{ application: OrganizerApplication }>(
      `/admin/exhibitor-applications/${id}/approve`,
      { token, method: "POST" }
    ),
  rejectOrganizerApplication: (token: string, id: string, reason?: string) =>
    request<{ application: OrganizerApplication }>(
      `/admin/exhibitor-applications/${id}/reject`,
      {
        token,
        method: "POST",
        body: JSON.stringify({ reason: reason ?? "" }),
      }
    ),
};

export const inviteApi = {
  get: (token: string) => request<{ invite: AdminInvite }>(`/auth/invites/${token}`),
  accept: (token: string, payload: { name: string; password: string }) =>
    request<{ admin: AdminTeamMember }>(`/auth/invites/${token}/accept`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};
